/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import { OutlinedInput } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import Search from '@icon/Search';
import P2 from '@base/P2/P2';
import Close from '@icon/notes/Close';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import KeyboardArrowUp from '@icon/KeyboardArrowUp';
import debounce from 'lodash/debounce';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useRouter } from 'next/router';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import { EpubContext } from '../Context/EpubContext';
import { EPUB_NOTES, NOTES_SEARCH_CLICKED } from '../utils';

interface SearchNavigationProps {
  currentSearchPosition: number;
  totalSearchResults: number;
  onClose: () => void;
  onNavigation: (direction: string) => void;
  disableNavigation: boolean;
}

const SearchIconContainer = styled.div`
  height: ${Spacings.SPACING_6B};
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: ${Spacings.SPACING_4B};
`;

const StyP2 = styled(P2)`
  font-weight: ${Fonts.LIGHT};
  line-height: ${Spacings.SPACING_4B};
  height: 19px;
  width: max-content;
`;

const Divider = styled.div`
  width: ${Spacings.SPACING_1};
  height: 19px;
  background-color: var(--color-divider);
  margin-right: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_3B};
`;

const StyKeyboardArrowDown = styled(KeyboardArrowDown)`
  cursor: ${({ $cursor }) => $cursor};
`;

const StyKeyboardArrowUp = styled(KeyboardArrowUp)`
  cursor: ${({ $cursor }) => $cursor};
  margin-right: ${Spacings.SPACING_1B};
`;

const StyCloseIcon = styled(Close)`
  margin-left: ${Spacings.SPACING_4B};
  color: var(--color-base-fill);
  cursor: pointer;
`;

const SearchTextFiled = styled(OutlinedInput)`
  &.MuiInputBase-root {
    width: 408px;
    height: ${Spacings.SPACING_11B};
  }

  &.MuiOutlinedInput-root {
    border-radius: ${Spacings.SPACING_6};
  }

  .MuiOutlinedInput-input {
    padding: ${Spacings.SPACING_3B} 0 ${Spacings.SPACING_3B} 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border: ${Spacings.SPACING_1} solid var(--color-divider);
  }
  &:hover .MuiOutlinedInput-notchedOutline {
    border: ${Spacings.SPACING_1} solid var(--color-divider);
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: ${Spacings.SPACING_1} solid var(--color-base-fill);
  }

  &.MuiOutlinedInput-adornedEnd {
    padding-right: ${Spacings.SPACING_4B};
  }

  &.MuiOutlinedInput-adornedStart {
    padding-left: ${Spacings.SPACING_4B};
  }

  .MuiInputBase-input {
    color: var(--color-base-fill);
    font-weight: ${Fonts.LIGHT};
    font-size: 14px;
    line-height: ${Spacings.SPACING_5B};
    height: ${Spacings.SPACING_5B};
  }
`;

const SearchIcon = () => (
  <InputAdornment position="start">
    <SearchIconContainer>
      <Search size={Spacings.SPACING_6B} color="var(--color-text-secondary)" />
    </SearchIconContainer>
  </InputAdornment>
);

const SearchNavigation: React.FC<SearchNavigationProps> = ({
  totalSearchResults,
  currentSearchPosition,
  disableNavigation,
  onClose,
  onNavigation
}) => (
  <NavigationWrapper>
    <StyP2 color="var(--color-text-secondary)">
      {totalSearchResults > 0 ? `${currentSearchPosition}/` : null}
      {totalSearchResults}
    </StyP2>
    <Divider />
    <StyKeyboardArrowUp
      $cursor={
        currentSearchPosition === 1 ||
        totalSearchResults === 0 ||
        disableNavigation
          ? 'default'
          : 'pointer'
      }
      color={
        currentSearchPosition === 1 ||
        totalSearchResults === 0 ||
        disableNavigation
          ? 'var(--color-divider)'
          : 'var(--color-base-fill)'
      }
      height={Spacings.SPACING_6B}
      width={Spacings.SPACING_6B}
      onClick={() => currentSearchPosition !== 1 && onNavigation('up')}
    />
    <StyKeyboardArrowDown
      $cursor={
        currentSearchPosition === totalSearchResults ||
        totalSearchResults === 0 ||
        disableNavigation
          ? 'default'
          : 'pointer'
      }
      color={
        currentSearchPosition === totalSearchResults ||
        totalSearchResults === 0 ||
        disableNavigation
          ? 'var(--color-divider)'
          : 'var(--color-base-fill)'
      }
      height={Spacings.SPACING_6B}
      width={Spacings.SPACING_6B}
      onClick={() =>
        currentSearchPosition !== totalSearchResults && onNavigation('down')
      }
    />
    <StyCloseIcon
      onClick={onClose}
      color="var(--color-base-fill)"
      height={Spacings.SPACING_6B}
      width={Spacings.SPACING_6B}
    />
  </NavigationWrapper>
);

const SearchBox = () => {
  const {
    query: { goalUID, topicUID }
  } = useRouter();
  const [value, setValue] = useState('');
  const {
    searchObj,
    onSearchChange,
    onNavigation,
    onChangeSearchPosition,
    currentNotesUID,
    selectedConcept,
    disableNavigation
  } = useContext(EpubContext);

  const [hideSearchIcon, setHideSearchIcon] = useState(false);

  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });

  const { currentSearchPosition, totalSearchResults, searchTerm } = searchObj;

  const onFocus = () => {
    segment.track(
      NOTES_SEARCH_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_uid: currentNotesUID,
        notes_topic: topicUID
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setHideSearchIcon(true);
  };

  const onBlur = () => {
    if (!value) setHideSearchIcon(false);
  };

  const onClose = () => {
    setValue('');
    onSearchChange('');
  };

  const onSearch = (value) => {
    onSearchChange(value);
  };

  const onChange = (e) => {
    e.stopPropagation();
    const val = e.target.value;
    setValue(val);

    if (val.length > 2) {
      debouncedSearch.cancel();
      debouncedSearchTwo(val);
    } else {
      debouncedSearchTwo.cancel();
      debouncedSearch(val);
    }
  };

  const onKeyPress = ({ key }) => {
    if (
      key === 'Enter' &&
      totalSearchResults > currentSearchPosition &&
      !disableNavigation
    ) {
      onChangeSearchPosition(currentSearchPosition + 1);
    }
  };

  const debouncedSearchTwo = useCallback(debounce(onSearch, 500), []);

  const debouncedSearch = useCallback(debounce(onSearch, 2000), []);

  useEffect(() => {
    if (!searchTerm) setValue('');
  }, [searchTerm]);

  useEffect(() => {
    setHideSearchIcon(!!value);
  }, [value]);

  return (
    <Wrapper>
      <SearchTextFiled
        placeholder={`Find in ${selectedConcept?.name}`}
        startAdornment={!hideSearchIcon ? <SearchIcon /> : <></>}
        value={value}
        id="noteSearcg"
        autoComplete="off"
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        onBlur={onBlur}
        onChange={onChange}
        name="Epub-Search"
        endAdornment={
          hideSearchIcon && value ? (
            <SearchNavigation
              totalSearchResults={totalSearchResults}
              currentSearchPosition={currentSearchPosition}
              onClose={onClose}
              onNavigation={onNavigation}
              disableNavigation={disableNavigation}
            />
          ) : (
            <></>
          )
        }
        className="search-dd-input-wrapper"
      />
    </Wrapper>
  );
};

export default SearchBox;
